import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axiosInstance from '../axiosInstance';
import { useAuth } from './AuthContext';

/**
 * Settings Context
 * 
 * Provides a centralized cache for organization settings to avoid
 * re-fetching on every component mount. Settings are cached and
 * can be refreshed on-demand or after updates.
 */

const SettingsContext = createContext(null);

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Local storage key for persistent cache
const STORAGE_KEY = 'org_settings_cache';

const DEFAULT_SETTINGS = {
    extraHoursSettings: { enabled: false, rules: [] },
    weeklyOvertimeSettings: { enabled: false, rules: [] },
    fridayOvertimeSettings: { enabled: false, rules: [] },
    shabbatOvertimeSettings: { enabled: false, rules: [] },
    nightShiftSettings: {
        enabled: false,
        timeRange: { startHour: '22:00', endHour: '06:00' },
        minimumHours: 2,
        rules: [],
        overtimeRules: []
    },
    breakSettings: {
        enabled: true,
        rules: [
            { afterHours: 6, breakDuration: 0.5 } // Default: after 6 hours, 30 minutes break
        ]
    },
    displaySettings: {
        fridayColor: '#fff3cd',
        holidaySaturdayColor: '#d1ecf1'
    },
    fullContractorSettings: {
        minimumWorkHours: 0
    },
    shiftCapSettings: {
        capHours: 12
    },
    // "שעות נמוכות באתר": the salary report flags an employee↔site assignment
    // with fewer hours than this. Keep in sync with the server subschema
    // (models/OrganizationSettings.js) and server/utils/lowSiteHoursSettings.js —
    // server/utils/lowSiteHoursSettingsLayers.test.js fails if any layer drifts.
    lowSiteHoursSettings: {
        thresholdHours: 2
    },
    // Whether employees see their hours and past (signed/disputed) reports in
    // the mobile app / magic-link page. Read-side only — see
    // server/utils/mobileHoursVisibility.js; the layers test there fails if any
    // layer (model, merge, both normalizers, this default, SettingsPage) drifts.
    mobileAppSettings: { employeeHistory: 'all' },
    // "הפסקה בתשלום במשמרת ארוכה" — REPORT-ONLY column (client/src/utils/paidBreak.js).
    // Keep in sync with models/OrganizationSettings.js and
    // server/utils/paidBreakSettings.js — server/utils/paidBreakSettingsLayers.test.js
    // fails if any layer drifts.
    paidBreakSettings: { enabled: false, afterHours: 11, breakMinutes: 15 },
    approvalLinkSettings: {
        expiryDays: 1
    },
    timesheetLockSettings: {
        enabled: true
    },
    emailNotificationSettings: {
        recipients: [],
        disputeAlert: { enabled: false, subject: 'New Dispute — {{worker_name}}', body: '' },
        signedAlert: { enabled: false, subject: 'Report Signed — {{worker_name}}', body: '' },
        serviceCallAlert: { enabled: true }
    },
    apartmentSettings: {
        groundServiceCompany: {
            name: '',
            companyNumber: '',
            address: '',
            contactName: '',
            phone: '',
            email: ''
        },
        commissionPercent: 8,
        // Apartments-module VAT rate: the default for a NEW expense AND the VAT
        // charged on the ground-service total (after the commission). Each
        // expense still stores its own vatRate, so changing this never re-rates
        // stored rows. Keep in sync with the server subschema.
        vatPercent: 18,
        commissionExcludedTypes: ['ציוד', 'הובלה'],
        // Admin-managed expense category list (staging bug [14]). EMPTY = not
        // configured yet → the expense form keeps its free-text datalist.
        expenseCategories: [],
        // Categories filed at 0% VAT (ארנונה by default — see
        // components/apartments/vatExemptCategories.js). Empty ⇒ that default.
        vatExemptCategories: [],
        // Service-call notification block (spec §13, staging bug [27]). Separate
        // from the timesheet emailNotificationSettings above: its own address
        // list + four independent event toggles. Defaults: open ON, close ON,
        // status-change OFF, done OFF. Keep in sync with the server subschema
        // (models/OrganizationSettings.js) and utils/serviceCallNotify.js.
        serviceCallEmail: {
            primaryEmail: '',
            additionalEmails: [],
            onOpen: true,
            onStatusChange: false,
            onDone: false,
            onClose: true,
            onInspectionSubmitted: true
        },
        // Contract / vacate reminder emails. Recipients are PER ALERT TYPE (the
        // client asked for exactly that: "ולסוגי המיילים השונים"), so each type
        // owns its toggle, its addresses and its day-before thresholds — the
        // lease-renewal mail and the handover mail reach different people.
        //
        // Keep in sync with the server subschema (models/OrganizationSettings.js),
        // the resolver defaults (server/utils/contractReminders.js) and BOTH
        // normalizers below. server/utils/contractReminderSettingsLayers.test.js
        // fails if any one of those layers is missing a leaf (CLAUDE.md 92e9967:
        // a field absent from one layer silently reverts to its default).
        //
        // Enabled by default with EMPTY address lists, which is what makes the
        // block safe to ship: the daily job resolves `no_recipients` and mails
        // nobody until an operator types an address in.
        contractReminders: {
            contractExpiry: {
                enabled: true,
                primaryEmail: '',
                additionalEmails: [],
                daysBefore: [60, 30, 7]
            },
            vacateDate: {
                enabled: true,
                primaryEmail: '',
                additionalEmails: [],
                daysBefore: [14, 3]
            }
        }
    },
    // E-signing module (spec §2.8). Org-level DEFAULTS for a new document —
    // every one of them is frozen onto a copy when it is sent, so editing them
    // here never changes a document already out for signature.
    //
    // Keep in sync with the server subschema (models/OrganizationSettings.js),
    // the lean-read resolver (server/utils/signingSettings.js), the write
    // builder (server/utils/signingSettingsUpdate.js) and BOTH normalizers
    // below. server/utils/signingSettingsLayers.test.js fails if any one of
    // those layers is missing a leaf (92e9967: a field absent from one layer
    // silently reverts to its default on every save).
    signingSettings: {
        reminders: {
            enabled: true,
            everyDays: 2,
            // 0 = no automatic reminders at all — never "unlimited".
            maxCount: 3,
            quietHours: { from: 21, to: 8 },
            skipShabbat: true
        },
        // 0 = the link never expires.
        linkExpiryDays: 30,
        requireOtpDefault: true,
        replyEmailDefault: '',
        companyStamp: { name: '', businessId: '' }
    }
};

/**
 * Load cached settings from localStorage
 */
const loadFromStorage = () => {
    try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            // Check if cache is still valid
            if (Date.now() - timestamp < CACHE_DURATION) {
                return data;
            }
        }
    } catch (e) {
        console.warn('Failed to load settings from cache:', e);
    }
    return null;
};

/**
 * Save settings to localStorage
 */
const saveToStorage = (data) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            data,
            timestamp: Date.now()
        }));
    } catch (e) {
        console.warn('Failed to save settings to cache:', e);
    }
};

/**
 * Clear settings from localStorage
 */
const clearStorage = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        console.warn('Failed to clear settings cache:', e);
    }
};

export const SettingsProvider = ({ children }) => {
    // Field supervisors are fenced off /api/settings server-side (supervisorScope.js).
    // They never render a settings-dependent screen, so fetching it only logs a
    // harmless-but-alarming 403 in the console. Skip the request for them.
    const { user } = useAuth();
    const isFieldSupervisor = user?.role === 'field_supervisor';

    // Initialize with cached data if available
    const [settings, setSettings] = useState(() => loadFromStorage()?.settings || null);
    const [organization, setOrganization] = useState(() => loadFromStorage()?.organization || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);
    
    // Track if initial fetch has been done
    const initialFetchDone = useRef(false);
    
    // Fetch settings from API
    const fetchSettings = useCallback(async (force = false) => {
        // Field supervisors can't read settings (403); never hit the endpoint.
        if (isFieldSupervisor) {
            return { settings, organization };
        }
        // Skip if recently fetched (unless forced)
        if (!force && lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
            return { settings, organization };
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await axiosInstance.get('/settings');
            if (response.data.success) {
                const { settings: newSettings, organization: newOrg } = response.data.data;
                
                // Normalize settings with defaults
                const normalizedSettings = {
                    extraHoursSettings: {
                        enabled: newSettings?.extraHoursSettings?.enabled || false,
                        rules: newSettings?.extraHoursSettings?.rules || []
                    },
                    weeklyOvertimeSettings: {
                        enabled: newSettings?.weeklyOvertimeSettings?.enabled || false,
                        rules: newSettings?.weeklyOvertimeSettings?.rules || []
                    },
                    fridayOvertimeSettings: {
                        enabled: newSettings?.fridayOvertimeSettings?.enabled || false,
                        rules: newSettings?.fridayOvertimeSettings?.rules || []
                    },
                    shabbatOvertimeSettings: {
                        enabled: newSettings?.shabbatOvertimeSettings?.enabled || false,
                        rules: newSettings?.shabbatOvertimeSettings?.rules || []
                    },
                    nightShiftSettings: {
                        enabled: newSettings?.nightShiftSettings?.enabled || false,
                        timeRange: {
                            startHour: newSettings?.nightShiftSettings?.timeRange?.startHour || '22:00',
                            endHour: newSettings?.nightShiftSettings?.timeRange?.endHour || '06:00'
                        },
                        minimumHours: newSettings?.nightShiftSettings?.minimumHours || 2,
                        rules: newSettings?.nightShiftSettings?.rules || [],
                        overtimeRules: newSettings?.nightShiftSettings?.overtimeRules || []
                    },
                    breakSettings: {
                        enabled: newSettings?.breakSettings?.enabled !== undefined ? newSettings.breakSettings.enabled : DEFAULT_SETTINGS.breakSettings.enabled,
                        rules: newSettings?.breakSettings?.rules || DEFAULT_SETTINGS.breakSettings.rules
                    },
                    displaySettings: {
                        fridayColor: newSettings?.displaySettings?.fridayColor || DEFAULT_SETTINGS.displaySettings.fridayColor,
                        holidaySaturdayColor: newSettings?.displaySettings?.holidaySaturdayColor || DEFAULT_SETTINGS.displaySettings.holidaySaturdayColor
                    },
                    fullContractorSettings: {
                        minimumWorkHours: newSettings?.fullContractorSettings?.minimumWorkHours ?? DEFAULT_SETTINGS.fullContractorSettings.minimumWorkHours
                    },
                    shiftCapSettings: {
                        capHours: newSettings?.shiftCapSettings?.capHours ?? DEFAULT_SETTINGS.shiftCapSettings.capHours
                    },
                    // `??` not `||`: 0 is a legal threshold and must survive.
                    lowSiteHoursSettings: {
                        thresholdHours: newSettings?.lowSiteHoursSettings?.thresholdHours ?? DEFAULT_SETTINGS.lowSiteHoursSettings.thresholdHours
                    },
                    // `??` not `||`: false is the whole point of this toggle.
                    mobileAppSettings: { employeeHistory: newSettings?.mobileAppSettings?.employeeHistory ?? DEFAULT_SETTINGS.mobileAppSettings.employeeHistory },
                    // `??` not `||`: false / 0 are legal values and must survive.
                    paidBreakSettings: {
                        enabled: newSettings?.paidBreakSettings?.enabled ?? DEFAULT_SETTINGS.paidBreakSettings.enabled,
                        afterHours: newSettings?.paidBreakSettings?.afterHours ?? DEFAULT_SETTINGS.paidBreakSettings.afterHours,
                        breakMinutes: newSettings?.paidBreakSettings?.breakMinutes ?? DEFAULT_SETTINGS.paidBreakSettings.breakMinutes
                    },
                    smsApprovalTemplate: newSettings?.smsApprovalTemplate ?? '',
                    logoUrl: newSettings?.logoUrl ?? '',
                    // Rebuilt key-by-key (CLAUDE.md whitelist rule): a leaf missing
                    // from the server response must fall back to its DEFAULT, never
                    // vanish. serviceCallAlert.enabled defaults to TRUE, so orgs whose
                    // stored doc predates the field keep receiving service-call alerts.
                    emailNotificationSettings: {
                        recipients: Array.isArray(newSettings?.emailNotificationSettings?.recipients)
                            ? newSettings.emailNotificationSettings.recipients
                            : DEFAULT_SETTINGS.emailNotificationSettings.recipients,
                        disputeAlert: {
                            enabled: newSettings?.emailNotificationSettings?.disputeAlert?.enabled ?? DEFAULT_SETTINGS.emailNotificationSettings.disputeAlert.enabled,
                            subject: newSettings?.emailNotificationSettings?.disputeAlert?.subject ?? DEFAULT_SETTINGS.emailNotificationSettings.disputeAlert.subject,
                            body: newSettings?.emailNotificationSettings?.disputeAlert?.body ?? DEFAULT_SETTINGS.emailNotificationSettings.disputeAlert.body
                        },
                        signedAlert: {
                            enabled: newSettings?.emailNotificationSettings?.signedAlert?.enabled ?? DEFAULT_SETTINGS.emailNotificationSettings.signedAlert.enabled,
                            subject: newSettings?.emailNotificationSettings?.signedAlert?.subject ?? DEFAULT_SETTINGS.emailNotificationSettings.signedAlert.subject,
                            body: newSettings?.emailNotificationSettings?.signedAlert?.body ?? DEFAULT_SETTINGS.emailNotificationSettings.signedAlert.body
                        },
                        serviceCallAlert: {
                            enabled: newSettings?.emailNotificationSettings?.serviceCallAlert?.enabled ?? DEFAULT_SETTINGS.emailNotificationSettings.serviceCallAlert.enabled
                        }
                    },
                    whatsAppSettings: newSettings?.whatsAppSettings ?? { allowedNumbers: [] },
                    approvalLinkSettings: {
                        expiryDays: newSettings?.approvalLinkSettings?.expiryDays ?? DEFAULT_SETTINGS.approvalLinkSettings.expiryDays
                    },
                    timesheetLockSettings: {
                        enabled: newSettings?.timesheetLockSettings?.enabled ?? DEFAULT_SETTINGS.timesheetLockSettings.enabled
                    },
                    apartmentSettings: {
                        groundServiceCompany: {
                            name: newSettings?.apartmentSettings?.groundServiceCompany?.name ?? '',
                            companyNumber: newSettings?.apartmentSettings?.groundServiceCompany?.companyNumber ?? '',
                            address: newSettings?.apartmentSettings?.groundServiceCompany?.address ?? '',
                            contactName: newSettings?.apartmentSettings?.groundServiceCompany?.contactName ?? '',
                            phone: newSettings?.apartmentSettings?.groundServiceCompany?.phone ?? '',
                            email: newSettings?.apartmentSettings?.groundServiceCompany?.email ?? ''
                        },
                        commissionPercent: newSettings?.apartmentSettings?.commissionPercent ?? DEFAULT_SETTINGS.apartmentSettings.commissionPercent,
                        vatPercent: newSettings?.apartmentSettings?.vatPercent ?? DEFAULT_SETTINGS.apartmentSettings.vatPercent,
                        commissionExcludedTypes: Array.isArray(newSettings?.apartmentSettings?.commissionExcludedTypes)
                            ? newSettings.apartmentSettings.commissionExcludedTypes
                            : DEFAULT_SETTINGS.apartmentSettings.commissionExcludedTypes,
                        // Admin-managed expense categories (bug [14]) + service-call
                        // email block (bug [27]) — key-by-key so a leaf missing from
                        // the server response falls back to DEFAULT, never vanishes.
                        // Booleans use `??` so a stored explicit false survives.
                        expenseCategories: Array.isArray(newSettings?.apartmentSettings?.expenseCategories)
                            ? newSettings.apartmentSettings.expenseCategories
                            : DEFAULT_SETTINGS.apartmentSettings.expenseCategories,
                        vatExemptCategories: Array.isArray(newSettings?.apartmentSettings?.vatExemptCategories)
                            ? newSettings.apartmentSettings.vatExemptCategories
                            : DEFAULT_SETTINGS.apartmentSettings.vatExemptCategories,
                        serviceCallEmail: {
                            primaryEmail: newSettings?.apartmentSettings?.serviceCallEmail?.primaryEmail ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.primaryEmail,
                            additionalEmails: Array.isArray(newSettings?.apartmentSettings?.serviceCallEmail?.additionalEmails)
                                ? newSettings.apartmentSettings.serviceCallEmail.additionalEmails
                                : DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.additionalEmails,
                            onOpen: newSettings?.apartmentSettings?.serviceCallEmail?.onOpen ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.onOpen,
                            onStatusChange: newSettings?.apartmentSettings?.serviceCallEmail?.onStatusChange ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.onStatusChange,
                            onDone: newSettings?.apartmentSettings?.serviceCallEmail?.onDone ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.onDone,
                            onClose: newSettings?.apartmentSettings?.serviceCallEmail?.onClose ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.onClose,
                            onInspectionSubmitted: newSettings?.apartmentSettings?.serviceCallEmail?.onInspectionSubmitted ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.onInspectionSubmitted
                        },
                        // Contract / vacate reminders — same leaf-by-leaf rebuild.
                        // An org document written before the feature has no
                        // contractReminders key at all, so every leaf falls back
                        // to DEFAULT_SETTINGS; `??` (not ||) so a stored explicit
                        // false / empty threshold list survives.
                        contractReminders: {
                            contractExpiry: {
                                enabled: newSettings?.apartmentSettings?.contractReminders?.contractExpiry?.enabled ?? DEFAULT_SETTINGS.apartmentSettings.contractReminders.contractExpiry.enabled,
                                primaryEmail: newSettings?.apartmentSettings?.contractReminders?.contractExpiry?.primaryEmail ?? DEFAULT_SETTINGS.apartmentSettings.contractReminders.contractExpiry.primaryEmail,
                                additionalEmails: Array.isArray(newSettings?.apartmentSettings?.contractReminders?.contractExpiry?.additionalEmails)
                                    ? newSettings.apartmentSettings.contractReminders.contractExpiry.additionalEmails
                                    : DEFAULT_SETTINGS.apartmentSettings.contractReminders.contractExpiry.additionalEmails,
                                daysBefore: Array.isArray(newSettings?.apartmentSettings?.contractReminders?.contractExpiry?.daysBefore)
                                    ? newSettings.apartmentSettings.contractReminders.contractExpiry.daysBefore
                                    : DEFAULT_SETTINGS.apartmentSettings.contractReminders.contractExpiry.daysBefore
                            },
                            vacateDate: {
                                enabled: newSettings?.apartmentSettings?.contractReminders?.vacateDate?.enabled ?? DEFAULT_SETTINGS.apartmentSettings.contractReminders.vacateDate.enabled,
                                primaryEmail: newSettings?.apartmentSettings?.contractReminders?.vacateDate?.primaryEmail ?? DEFAULT_SETTINGS.apartmentSettings.contractReminders.vacateDate.primaryEmail,
                                additionalEmails: Array.isArray(newSettings?.apartmentSettings?.contractReminders?.vacateDate?.additionalEmails)
                                    ? newSettings.apartmentSettings.contractReminders.vacateDate.additionalEmails
                                    : DEFAULT_SETTINGS.apartmentSettings.contractReminders.vacateDate.additionalEmails,
                                daysBefore: Array.isArray(newSettings?.apartmentSettings?.contractReminders?.vacateDate?.daysBefore)
                                    ? newSettings.apartmentSettings.contractReminders.vacateDate.daysBefore
                                    : DEFAULT_SETTINGS.apartmentSettings.contractReminders.vacateDate.daysBefore
                            }
                        }
                    },
                    // E-signing defaults — same leaf-by-leaf rebuild. An org
                    // document written before the module has no signingSettings
                    // key at all, so every leaf falls back to DEFAULT_SETTINGS;
                    // `??` (not ||) so a stored explicit false, or a stored 0
                    // (which means "no reminders" / "no expiry"), survives.
                    signingSettings: {
                        reminders: {
                            enabled: newSettings?.signingSettings?.reminders?.enabled ?? DEFAULT_SETTINGS.signingSettings.reminders.enabled,
                            everyDays: newSettings?.signingSettings?.reminders?.everyDays ?? DEFAULT_SETTINGS.signingSettings.reminders.everyDays,
                            maxCount: newSettings?.signingSettings?.reminders?.maxCount ?? DEFAULT_SETTINGS.signingSettings.reminders.maxCount,
                            quietHours: {
                                from: newSettings?.signingSettings?.reminders?.quietHours?.from ?? DEFAULT_SETTINGS.signingSettings.reminders.quietHours.from,
                                to: newSettings?.signingSettings?.reminders?.quietHours?.to ?? DEFAULT_SETTINGS.signingSettings.reminders.quietHours.to
                            },
                            skipShabbat: newSettings?.signingSettings?.reminders?.skipShabbat ?? DEFAULT_SETTINGS.signingSettings.reminders.skipShabbat
                        },
                        linkExpiryDays: newSettings?.signingSettings?.linkExpiryDays ?? DEFAULT_SETTINGS.signingSettings.linkExpiryDays,
                        requireOtpDefault: newSettings?.signingSettings?.requireOtpDefault ?? DEFAULT_SETTINGS.signingSettings.requireOtpDefault,
                        replyEmailDefault: newSettings?.signingSettings?.replyEmailDefault ?? DEFAULT_SETTINGS.signingSettings.replyEmailDefault,
                        companyStamp: {
                            name: newSettings?.signingSettings?.companyStamp?.name ?? DEFAULT_SETTINGS.signingSettings.companyStamp.name,
                            businessId: newSettings?.signingSettings?.companyStamp?.businessId ?? DEFAULT_SETTINGS.signingSettings.companyStamp.businessId
                        }
                    }
                };

                setSettings(normalizedSettings);
                setOrganization(newOrg);
                setLastFetched(Date.now());
                
                // Save to storage for persistence
                saveToStorage({ settings: normalizedSettings, organization: newOrg });
                
                return { settings: normalizedSettings, organization: newOrg };
            } else {
                throw new Error(response.data.message || 'Failed to load settings');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to load settings';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [settings, organization, lastFetched, isFieldSupervisor]);
    
    // Update settings and sync with cache
    const updateSettings = useCallback(async (newSettings) => {
        setLoading(true);
        setError(null);
        
        console.log('[SettingsContext] Saving settings:', newSettings);
        
        try {
            const response = await axiosInstance.put('/settings', newSettings);
            if (response.data.success) {
                const { settings: updatedSettings, organization: updatedOrg } = response.data.data;
                
                console.log('[SettingsContext] Server returned settings:', updatedSettings);
                console.log('[SettingsContext] weeklyOvertimeSettings from server:', updatedSettings?.weeklyOvertimeSettings);
                
                // Normalize and update state
                const normalizedSettings = {
                    extraHoursSettings: updatedSettings?.extraHoursSettings || settings?.extraHoursSettings || DEFAULT_SETTINGS.extraHoursSettings,
                    weeklyOvertimeSettings: updatedSettings?.weeklyOvertimeSettings || settings?.weeklyOvertimeSettings || DEFAULT_SETTINGS.weeklyOvertimeSettings,
                    fridayOvertimeSettings: updatedSettings?.fridayOvertimeSettings || settings?.fridayOvertimeSettings || DEFAULT_SETTINGS.fridayOvertimeSettings,
                    shabbatOvertimeSettings: updatedSettings?.shabbatOvertimeSettings || settings?.shabbatOvertimeSettings || DEFAULT_SETTINGS.shabbatOvertimeSettings,
                    nightShiftSettings: updatedSettings?.nightShiftSettings || settings?.nightShiftSettings || DEFAULT_SETTINGS.nightShiftSettings,
                    breakSettings: updatedSettings?.breakSettings || settings?.breakSettings || DEFAULT_SETTINGS.breakSettings,
                    displaySettings: updatedSettings?.displaySettings || settings?.displaySettings || DEFAULT_SETTINGS.displaySettings,
                    fullContractorSettings: updatedSettings?.fullContractorSettings || settings?.fullContractorSettings || DEFAULT_SETTINGS.fullContractorSettings,
                    shiftCapSettings: updatedSettings?.shiftCapSettings || settings?.shiftCapSettings || DEFAULT_SETTINGS.shiftCapSettings,
                    // Server response → current state → DEFAULT, `??` so a stored
                    // 0 survives a save (the 92e9967 shape is exactly a leaf this
                    // normalizer forgets).
                    lowSiteHoursSettings: {
                        thresholdHours: updatedSettings?.lowSiteHoursSettings?.thresholdHours ?? settings?.lowSiteHoursSettings?.thresholdHours ?? DEFAULT_SETTINGS.lowSiteHoursSettings.thresholdHours
                    },
                    mobileAppSettings: { employeeHistory: updatedSettings?.mobileAppSettings?.employeeHistory ?? settings?.mobileAppSettings?.employeeHistory ?? DEFAULT_SETTINGS.mobileAppSettings.employeeHistory },
                    paidBreakSettings: {
                        enabled: updatedSettings?.paidBreakSettings?.enabled ?? settings?.paidBreakSettings?.enabled ?? DEFAULT_SETTINGS.paidBreakSettings.enabled,
                        afterHours: updatedSettings?.paidBreakSettings?.afterHours ?? settings?.paidBreakSettings?.afterHours ?? DEFAULT_SETTINGS.paidBreakSettings.afterHours,
                        breakMinutes: updatedSettings?.paidBreakSettings?.breakMinutes ?? settings?.paidBreakSettings?.breakMinutes ?? DEFAULT_SETTINGS.paidBreakSettings.breakMinutes
                    },
                    smsApprovalTemplate: updatedSettings?.smsApprovalTemplate ?? settings?.smsApprovalTemplate ?? '',
                    logoUrl: updatedSettings?.logoUrl ?? settings?.logoUrl ?? '',
                    // Same key-by-key rebuild as the GET path, with the extra
                    // current-state tier: server response → current state → DEFAULT
                    // (mirrors approvalLinkSettings.expiryDays below).
                    emailNotificationSettings: {
                        recipients: Array.isArray(updatedSettings?.emailNotificationSettings?.recipients)
                            ? updatedSettings.emailNotificationSettings.recipients
                            : (Array.isArray(settings?.emailNotificationSettings?.recipients)
                                ? settings.emailNotificationSettings.recipients
                                : DEFAULT_SETTINGS.emailNotificationSettings.recipients),
                        disputeAlert: {
                            enabled: updatedSettings?.emailNotificationSettings?.disputeAlert?.enabled ?? settings?.emailNotificationSettings?.disputeAlert?.enabled ?? DEFAULT_SETTINGS.emailNotificationSettings.disputeAlert.enabled,
                            subject: updatedSettings?.emailNotificationSettings?.disputeAlert?.subject ?? settings?.emailNotificationSettings?.disputeAlert?.subject ?? DEFAULT_SETTINGS.emailNotificationSettings.disputeAlert.subject,
                            body: updatedSettings?.emailNotificationSettings?.disputeAlert?.body ?? settings?.emailNotificationSettings?.disputeAlert?.body ?? DEFAULT_SETTINGS.emailNotificationSettings.disputeAlert.body
                        },
                        signedAlert: {
                            enabled: updatedSettings?.emailNotificationSettings?.signedAlert?.enabled ?? settings?.emailNotificationSettings?.signedAlert?.enabled ?? DEFAULT_SETTINGS.emailNotificationSettings.signedAlert.enabled,
                            subject: updatedSettings?.emailNotificationSettings?.signedAlert?.subject ?? settings?.emailNotificationSettings?.signedAlert?.subject ?? DEFAULT_SETTINGS.emailNotificationSettings.signedAlert.subject,
                            body: updatedSettings?.emailNotificationSettings?.signedAlert?.body ?? settings?.emailNotificationSettings?.signedAlert?.body ?? DEFAULT_SETTINGS.emailNotificationSettings.signedAlert.body
                        },
                        serviceCallAlert: {
                            enabled: updatedSettings?.emailNotificationSettings?.serviceCallAlert?.enabled ?? settings?.emailNotificationSettings?.serviceCallAlert?.enabled ?? DEFAULT_SETTINGS.emailNotificationSettings.serviceCallAlert.enabled
                        }
                    },
                    whatsAppSettings: updatedSettings?.whatsAppSettings ?? settings?.whatsAppSettings ?? { allowedNumbers: [] },
                    approvalLinkSettings: {
                        expiryDays: updatedSettings?.approvalLinkSettings?.expiryDays ?? settings?.approvalLinkSettings?.expiryDays ?? DEFAULT_SETTINGS.approvalLinkSettings.expiryDays
                    },
                    timesheetLockSettings: {
                        enabled: updatedSettings?.timesheetLockSettings?.enabled ?? settings?.timesheetLockSettings?.enabled ?? DEFAULT_SETTINGS.timesheetLockSettings.enabled
                    },
                    // Rebuilt key-by-key (CLAUDE.md whitelist rule), mirroring the
                    // GET path above: a leaf missing from the server response must
                    // fall back to its DEFAULT, never vanish.
                    apartmentSettings: {
                        groundServiceCompany: {
                            name: updatedSettings?.apartmentSettings?.groundServiceCompany?.name ?? settings?.apartmentSettings?.groundServiceCompany?.name ?? '',
                            companyNumber: updatedSettings?.apartmentSettings?.groundServiceCompany?.companyNumber ?? settings?.apartmentSettings?.groundServiceCompany?.companyNumber ?? '',
                            address: updatedSettings?.apartmentSettings?.groundServiceCompany?.address ?? settings?.apartmentSettings?.groundServiceCompany?.address ?? '',
                            contactName: updatedSettings?.apartmentSettings?.groundServiceCompany?.contactName ?? settings?.apartmentSettings?.groundServiceCompany?.contactName ?? '',
                            phone: updatedSettings?.apartmentSettings?.groundServiceCompany?.phone ?? settings?.apartmentSettings?.groundServiceCompany?.phone ?? '',
                            email: updatedSettings?.apartmentSettings?.groundServiceCompany?.email ?? settings?.apartmentSettings?.groundServiceCompany?.email ?? ''
                        },
                        commissionPercent: updatedSettings?.apartmentSettings?.commissionPercent ?? settings?.apartmentSettings?.commissionPercent ?? DEFAULT_SETTINGS.apartmentSettings.commissionPercent,
                        vatPercent: updatedSettings?.apartmentSettings?.vatPercent ?? settings?.apartmentSettings?.vatPercent ?? DEFAULT_SETTINGS.apartmentSettings.vatPercent,
                        commissionExcludedTypes: Array.isArray(updatedSettings?.apartmentSettings?.commissionExcludedTypes)
                            ? updatedSettings.apartmentSettings.commissionExcludedTypes
                            : (Array.isArray(settings?.apartmentSettings?.commissionExcludedTypes)
                                ? settings.apartmentSettings.commissionExcludedTypes
                                : DEFAULT_SETTINGS.apartmentSettings.commissionExcludedTypes),
                        // Same key-by-key rebuild as the GET path, with the extra
                        // current-state tier (server → current → DEFAULT).
                        expenseCategories: Array.isArray(updatedSettings?.apartmentSettings?.expenseCategories)
                            ? updatedSettings.apartmentSettings.expenseCategories
                            : (Array.isArray(settings?.apartmentSettings?.expenseCategories)
                                ? settings.apartmentSettings.expenseCategories
                                : DEFAULT_SETTINGS.apartmentSettings.expenseCategories),
                        vatExemptCategories: Array.isArray(updatedSettings?.apartmentSettings?.vatExemptCategories)
                            ? updatedSettings.apartmentSettings.vatExemptCategories
                            : (Array.isArray(settings?.apartmentSettings?.vatExemptCategories)
                                ? settings.apartmentSettings.vatExemptCategories
                                : DEFAULT_SETTINGS.apartmentSettings.vatExemptCategories),
                        serviceCallEmail: {
                            primaryEmail: updatedSettings?.apartmentSettings?.serviceCallEmail?.primaryEmail ?? settings?.apartmentSettings?.serviceCallEmail?.primaryEmail ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.primaryEmail,
                            additionalEmails: Array.isArray(updatedSettings?.apartmentSettings?.serviceCallEmail?.additionalEmails)
                                ? updatedSettings.apartmentSettings.serviceCallEmail.additionalEmails
                                : (Array.isArray(settings?.apartmentSettings?.serviceCallEmail?.additionalEmails)
                                    ? settings.apartmentSettings.serviceCallEmail.additionalEmails
                                    : DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.additionalEmails),
                            onOpen: updatedSettings?.apartmentSettings?.serviceCallEmail?.onOpen ?? settings?.apartmentSettings?.serviceCallEmail?.onOpen ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.onOpen,
                            onStatusChange: updatedSettings?.apartmentSettings?.serviceCallEmail?.onStatusChange ?? settings?.apartmentSettings?.serviceCallEmail?.onStatusChange ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.onStatusChange,
                            onDone: updatedSettings?.apartmentSettings?.serviceCallEmail?.onDone ?? settings?.apartmentSettings?.serviceCallEmail?.onDone ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.onDone,
                            onClose: updatedSettings?.apartmentSettings?.serviceCallEmail?.onClose ?? settings?.apartmentSettings?.serviceCallEmail?.onClose ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.onClose,
                            onInspectionSubmitted: updatedSettings?.apartmentSettings?.serviceCallEmail?.onInspectionSubmitted ?? settings?.apartmentSettings?.serviceCallEmail?.onInspectionSubmitted ?? DEFAULT_SETTINGS.apartmentSettings.serviceCallEmail.onInspectionSubmitted
                        },
                        // Contract / vacate reminders — same leaf-by-leaf rebuild
                        // as the GET path, with the extra current-state tier
                        // (server response → current state → DEFAULT). A leaf
                        // missing here reverts to its default on every save, which
                        // is exactly the 92e9967 approval-link-expiry bug.
                        contractReminders: {
                            contractExpiry: {
                                enabled: updatedSettings?.apartmentSettings?.contractReminders?.contractExpiry?.enabled ?? settings?.apartmentSettings?.contractReminders?.contractExpiry?.enabled ?? DEFAULT_SETTINGS.apartmentSettings.contractReminders.contractExpiry.enabled,
                                primaryEmail: updatedSettings?.apartmentSettings?.contractReminders?.contractExpiry?.primaryEmail ?? settings?.apartmentSettings?.contractReminders?.contractExpiry?.primaryEmail ?? DEFAULT_SETTINGS.apartmentSettings.contractReminders.contractExpiry.primaryEmail,
                                additionalEmails: Array.isArray(updatedSettings?.apartmentSettings?.contractReminders?.contractExpiry?.additionalEmails)
                                    ? updatedSettings.apartmentSettings.contractReminders.contractExpiry.additionalEmails
                                    : (Array.isArray(settings?.apartmentSettings?.contractReminders?.contractExpiry?.additionalEmails)
                                        ? settings.apartmentSettings.contractReminders.contractExpiry.additionalEmails
                                        : DEFAULT_SETTINGS.apartmentSettings.contractReminders.contractExpiry.additionalEmails),
                                daysBefore: Array.isArray(updatedSettings?.apartmentSettings?.contractReminders?.contractExpiry?.daysBefore)
                                    ? updatedSettings.apartmentSettings.contractReminders.contractExpiry.daysBefore
                                    : (Array.isArray(settings?.apartmentSettings?.contractReminders?.contractExpiry?.daysBefore)
                                        ? settings.apartmentSettings.contractReminders.contractExpiry.daysBefore
                                        : DEFAULT_SETTINGS.apartmentSettings.contractReminders.contractExpiry.daysBefore)
                            },
                            vacateDate: {
                                enabled: updatedSettings?.apartmentSettings?.contractReminders?.vacateDate?.enabled ?? settings?.apartmentSettings?.contractReminders?.vacateDate?.enabled ?? DEFAULT_SETTINGS.apartmentSettings.contractReminders.vacateDate.enabled,
                                primaryEmail: updatedSettings?.apartmentSettings?.contractReminders?.vacateDate?.primaryEmail ?? settings?.apartmentSettings?.contractReminders?.vacateDate?.primaryEmail ?? DEFAULT_SETTINGS.apartmentSettings.contractReminders.vacateDate.primaryEmail,
                                additionalEmails: Array.isArray(updatedSettings?.apartmentSettings?.contractReminders?.vacateDate?.additionalEmails)
                                    ? updatedSettings.apartmentSettings.contractReminders.vacateDate.additionalEmails
                                    : (Array.isArray(settings?.apartmentSettings?.contractReminders?.vacateDate?.additionalEmails)
                                        ? settings.apartmentSettings.contractReminders.vacateDate.additionalEmails
                                        : DEFAULT_SETTINGS.apartmentSettings.contractReminders.vacateDate.additionalEmails),
                                daysBefore: Array.isArray(updatedSettings?.apartmentSettings?.contractReminders?.vacateDate?.daysBefore)
                                    ? updatedSettings.apartmentSettings.contractReminders.vacateDate.daysBefore
                                    : (Array.isArray(settings?.apartmentSettings?.contractReminders?.vacateDate?.daysBefore)
                                        ? settings.apartmentSettings.contractReminders.vacateDate.daysBefore
                                        : DEFAULT_SETTINGS.apartmentSettings.contractReminders.vacateDate.daysBefore)
                            }
                        }
                    },
                    // E-signing defaults — same leaf-by-leaf rebuild as the GET
                    // path, with the extra current-state tier (server response →
                    // current state → DEFAULT). A leaf missing here reverts to
                    // its default on every save, which is exactly the 92e9967
                    // approval-link-expiry bug.
                    signingSettings: {
                        reminders: {
                            enabled: updatedSettings?.signingSettings?.reminders?.enabled ?? settings?.signingSettings?.reminders?.enabled ?? DEFAULT_SETTINGS.signingSettings.reminders.enabled,
                            everyDays: updatedSettings?.signingSettings?.reminders?.everyDays ?? settings?.signingSettings?.reminders?.everyDays ?? DEFAULT_SETTINGS.signingSettings.reminders.everyDays,
                            maxCount: updatedSettings?.signingSettings?.reminders?.maxCount ?? settings?.signingSettings?.reminders?.maxCount ?? DEFAULT_SETTINGS.signingSettings.reminders.maxCount,
                            quietHours: {
                                from: updatedSettings?.signingSettings?.reminders?.quietHours?.from ?? settings?.signingSettings?.reminders?.quietHours?.from ?? DEFAULT_SETTINGS.signingSettings.reminders.quietHours.from,
                                to: updatedSettings?.signingSettings?.reminders?.quietHours?.to ?? settings?.signingSettings?.reminders?.quietHours?.to ?? DEFAULT_SETTINGS.signingSettings.reminders.quietHours.to
                            },
                            skipShabbat: updatedSettings?.signingSettings?.reminders?.skipShabbat ?? settings?.signingSettings?.reminders?.skipShabbat ?? DEFAULT_SETTINGS.signingSettings.reminders.skipShabbat
                        },
                        linkExpiryDays: updatedSettings?.signingSettings?.linkExpiryDays ?? settings?.signingSettings?.linkExpiryDays ?? DEFAULT_SETTINGS.signingSettings.linkExpiryDays,
                        requireOtpDefault: updatedSettings?.signingSettings?.requireOtpDefault ?? settings?.signingSettings?.requireOtpDefault ?? DEFAULT_SETTINGS.signingSettings.requireOtpDefault,
                        replyEmailDefault: updatedSettings?.signingSettings?.replyEmailDefault ?? settings?.signingSettings?.replyEmailDefault ?? DEFAULT_SETTINGS.signingSettings.replyEmailDefault,
                        companyStamp: {
                            name: updatedSettings?.signingSettings?.companyStamp?.name ?? settings?.signingSettings?.companyStamp?.name ?? DEFAULT_SETTINGS.signingSettings.companyStamp.name,
                            businessId: updatedSettings?.signingSettings?.companyStamp?.businessId ?? settings?.signingSettings?.companyStamp?.businessId ?? DEFAULT_SETTINGS.signingSettings.companyStamp.businessId
                        }
                    }
                };
                
                console.log('[SettingsContext] Normalized settings:', normalizedSettings);
                console.log('[SettingsContext] weeklyOvertimeSettings after normalization:', normalizedSettings.weeklyOvertimeSettings);
                
                setSettings(normalizedSettings);
                setOrganization(updatedOrg);
                setLastFetched(Date.now());
                
                // Update cache
                saveToStorage({ settings: normalizedSettings, organization: updatedOrg });
                
                return { success: true, settings: normalizedSettings };
            } else {
                throw new Error(response.data.message || 'Failed to update settings');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to update settings';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [settings]);
    
    // Invalidate cache (useful after logout or organization change)
    const invalidateCache = useCallback(() => {
        clearStorage();
        setSettings(null);
        setOrganization(null);
        setLastFetched(null);
        initialFetchDone.current = false;
    }, []);
    
    // Get settings (returns cached or fetches)
    const getSettings = useCallback(async () => {
        if (settings) {
            return { settings, organization };
        }
        return fetchSettings();
    }, [settings, organization, fetchSettings]);
    
    // Auto-fetch on mount if not already fetched
    useEffect(() => {
        if (!initialFetchDone.current && !settings) {
            initialFetchDone.current = true;
            fetchSettings().catch(() => {
                // Silently fail on initial fetch - component will handle errors
            });
        }
    }, [fetchSettings, settings]);
    
    const value = {
        settings,
        organization,
        loading,
        error,
        lastFetched,
        fetchSettings,
        updateSettings,
        invalidateCache,
        getSettings,
        isStale: lastFetched ? Date.now() - lastFetched > CACHE_DURATION : true,
        DEFAULT_SETTINGS
    };
    
    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};

/**
 * Hook to use settings context
 * @returns {Object} Settings context value
 */
export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export default SettingsContext;
